import { useEffect, useState, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Plus, X, AlertTriangle, Dice5 } from "lucide-react";
import ManualQuestionPicker from "./ManualQuestionPicker";

/**
 * NewSessionDialog
 * Props: open, onOpenChange, classes, onCreated(session)
 * Implements:
 *  - class selector
 *  - per-topic quotas (multi-select + counts)
 *  - validation (sum == num_questions, available per topic)
 */
export default function NewSessionDialog({ open, onOpenChange, classes = [], onCreated, initialDraft = null, onDraftSaved }) {
  const [title, setTitle] = useState("Kartkówka z chemii");
  const [time, setTime] = useState(15);
  const [num, setNum] = useState(10);
  const [classId, setClassId] = useState("none");
  const [scope, setScope] = useState("basic_1");
  const [scopes, setScopes] = useState([]); // [{scope,label,question_count,active}]
  const [topicsMeta, setTopicsMeta] = useState([]); // [{topic, count}]
  // quotas: { [topic]: number }
  const [quotas, setQuotas] = useState({});
  const [creating, setCreating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftName, setDraftName] = useState(initialDraft?.name || "");
  const [draftId, setDraftId] = useState(initialDraft?.id || null);
  // Tryb wyboru pytań:
  //  - "auto": kwoty z bieżącego zakresu (klasycznie)
  //  - "cross": kwoty z per-tematową selekcją zakresu (wieloprzekrojowy)
  //  - "manual": ręcznie wybrane pytania z całej puli, dowolny zakres
  const [mode, setMode] = useState("auto");
  const [manualIds, setManualIds] = useState([]);
  const [manualIdsB, setManualIdsB] = useState([]);
  const [manualGroupTab, setManualGroupTab] = useState("A");   // "A" | "B"
  // { [topic]: scope_override } — używane tylko gdy mode === "cross"
  const [quotaScopes, setQuotaScopes] = useState({});
  const [randomizeOrder, setRandomizeOrder] = useState(false);
  const [groupsEnabled, setGroupsEnabled] = useState(false);

  const fetchedForOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      fetchedForOpen.current = false;
      return;
    }
    if (fetchedForOpen.current) return;
    fetchedForOpen.current = true;
    teacherApi.get("/scopes")
      .then(({ data }) => setScopes(data.scopes || []))
      .catch(() => toast.error("Nie udało się pobrać zakresów"));
    // Hydrate from draft (if any) — otherwise reset to defaults
    if (initialDraft) {
      setDraftId(initialDraft.id || null);
      setDraftName(initialDraft.name || "");
      setTitle(initialDraft.title || "Kartkówka z chemii");
      setTime(initialDraft.time_limit_minutes ?? 15);
      setNum(initialDraft.num_questions ?? 10);
      setClassId(initialDraft.class_id || "none");
      setScope(initialDraft.scope || "basic_1");
      const q = {};
      for (const tq of initialDraft.topic_quotas || []) q[tq.topic] = tq.count;
      setQuotas(q);
    } else {
      setDraftId(null);
      setDraftName("");
      setQuotas({});
    }
  }, [open, initialDraft]);

  // Reload topics whenever scope changes (or dialog opens). Only reset
  // quotas when the user actively changed the scope — not on initial open
  // hydrated from a draft (that would wipe restored quotas).
  const initialScopeRef = useRef(null);
  useEffect(() => {
    if (!open) { initialScopeRef.current = null; return; }
    teacherApi.get(`/questions/topics?scope=${scope}`)
      .then(({ data }) => setTopicsMeta(data.topics || []))
      .catch(() => toast.error("Nie udało się pobrać tematów"));
    if (initialScopeRef.current === null) {
      initialScopeRef.current = scope;
    } else if (initialScopeRef.current !== scope) {
      setQuotas({});
      initialScopeRef.current = scope;
    }
  }, [open, scope]);

  const availability = useMemo(() => {
    const m = {};
    for (const t of topicsMeta) m[t.topic] = t.count;
    return m;
  }, [topicsMeta]);

  const allocated = useMemo(
    () => Object.values(quotas).reduce((s, n) => s + (Number(n) || 0), 0),
    [quotas],
  );

  const numNum = Number(num) || 0;
  const remaining = numNum - allocated;
  const overAllocated = allocated > numNum;
  const exactlyMatched = allocated === numNum && allocated > 0;

  const setQuota = (topic, value) => {
    const v = Math.max(0, Math.floor(Number(value) || 0));
    const avail = availability[topic] ?? 0;
    const capped = Math.min(v, avail);
    setQuotas((prev) => {
      const next = { ...prev };
      if (capped === 0) delete next[topic];
      else next[topic] = capped;
      return next;
    });
  };

  const distributeEvenly = () => {
    const topics = Object.keys(quotas).filter((t) => quotas[t] !== undefined);
    if (topics.length === 0) {
      toast.info("Najpierw zaznacz tematy (kliknij +)");
      return;
    }
    const per = Math.floor(numNum / topics.length);
    const remainder = numNum - per * topics.length;
    const next = {};
    topics.forEach((t, idx) => {
      const base = per + (idx < remainder ? 1 : 0);
      const avail = availability[t] ?? 0;
      next[t] = Math.min(base, avail);
    });
    setQuotas(next);
  };

  const toggleTopic = (topic) => {
    setQuotas((prev) => {
      const next = { ...prev };
      if (topic in next) delete next[topic];
      else next[topic] = 0;
      return next;
    });
  };

  const create = async () => {
    if (!title.trim()) { toast.error("Podaj tytuł"); return; }
    // Manual mode has its own validation: pick 1..50 questions
    if (mode === "manual") {
      if (manualIds.length < 1) { toast.error("Wybierz przynajmniej jedno pytanie"); return; }
      if (manualIds.length > 50) { toast.error("Maksymalnie 50 pytań w kartkówce"); return; }
      if (groupsEnabled) {
        if (manualIdsB.length < 1) {
          toast.error("Grupy A/B: wybierz też pytania dla grupy B");
          return;
        }
        if (manualIdsB.length > 50) {
          toast.error("Grupa B: maksymalnie 50 pytań");
          return;
        }
        const overlap = manualIds.filter((x) => manualIdsB.includes(x));
        if (overlap.length > 0) {
          toast.error(`Grupa A i B mają wspólne pytania (${overlap.length}) — usuń je z jednej z grup`);
          return;
        }
      }
    } else {
      const useQuotas = Object.keys(quotas).length > 0;
      if (useQuotas && !exactlyMatched) {
        toast.error(`Suma pytań w tematach (${allocated}) musi równać się liczbie pytań (${numNum})`);
        return;
      }
    }
    setCreating(true);
    try {
      const payload = {
        title: title.trim(),
        time_limit_minutes: Number(time),
        num_questions: mode === "manual" ? manualIds.length : numNum,
        scope,
        randomize_order: randomizeOrder,
        groups_enabled: groupsEnabled,
      };
      if (classId && classId !== "none") payload.class_id = classId;
      if (mode === "manual") {
        payload.manual_question_ids = manualIds;
        if (groupsEnabled) {
          payload.manual_question_ids_b = manualIdsB;
        }
      } else {
        const useQuotas = Object.keys(quotas).length > 0;
        if (useQuotas) {
          payload.topic_quotas = Object.entries(quotas)
            .filter(([, c]) => c > 0)
            .map(([topic, count]) => {
              const row = { topic, count };
              // In cross-scope mode, attach the per-topic scope override
              // (backend defaults to session.scope when absent).
              if (mode === "cross" && quotaScopes[topic] && quotaScopes[topic] !== scope) {
                row.scope = quotaScopes[topic];
              }
              return row;
            });
        }
      }
      const { data } = await teacherApi.post("/sessions", payload);
      toast.success(`Sesja utworzona, kod: ${data.code}`);
      // If this session was materialized from a draft, remove that draft.
      if (draftId) {
        try { await teacherApi.delete(`/drafts/${draftId}`); } catch (e) { /* ignore */ }
      }
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd tworzenia sesji");
    } finally {
      setCreating(false);
    }
  };

  const saveDraft = async () => {
    if (overAllocated) {
      toast.error("Popraw kwoty — przydzielono więcej niż liczba pytań");
      return;
    }
    setSavingDraft(true);
    try {
      const payload = {
        name: (draftName || title || "Szkic bez nazwy").trim(),
        title: title.trim() || "Kartkówka z chemii",
        time_limit_minutes: Number(time),
        num_questions: numNum,
        scope,
      };
      if (classId && classId !== "none") payload.class_id = classId;
      const useQuotas = Object.keys(quotas).length > 0;
      if (useQuotas) {
        payload.topic_quotas = Object.entries(quotas)
          .filter(([, c]) => c > 0)
          .map(([topic, count]) => ({ topic, count }));
      }
      if (draftId) payload.id = draftId;
      const { data } = await teacherApi.post("/drafts", payload);
      setDraftId(data.id);
      setDraftName(data.name);
      toast.success(draftId ? "Szkic zaktualizowany" : "Szkic zapisany");
      onDraftSaved?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd zapisu szkicu");
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brut bg-white rounded-md border-2 border-[#0F172A] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl">Nowa kartkówka</DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            Skonfiguruj kartkówkę. Opcjonalnie przypisz do klasy i wybierz zakres tematów z per-tematowymi kwotami.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="font-bold text-sm">Tytuł</Label>
            <Input
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
              data-testid="new-session-title-input"
            />
          </div>

          <div>
            <Label className="font-bold text-sm">Zakres</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                data-testid="new-session-scope-select"
              >
                <SelectValue placeholder="Wybierz zakres" />
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-[#0F172A] rounded-md">
                {scopes.map((s) => (
                  <SelectItem
                    key={s.scope}
                    value={s.scope}
                    disabled={!s.active || s.question_count === 0}
                    data-testid={`new-session-scope-option-${s.scope}`}
                  >
                    {s.label} ({s.question_count} pytań{!s.active ? " — brak subskrypcji" : ""})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scopes.find((s) => s.scope === scope)?.question_count === 0 && (
              <div className="text-xs text-[#EF4444] font-bold mt-1" data-testid="scope-empty-warning">
                Ten zakres nie ma jeszcze pytań — wybierz inny lub dodaj pytania.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="font-bold text-sm">Czas (min)</Label>
              <Input
                type="number" min={1} max={180}
                value={time} onChange={(e) => setTime(e.target.value)}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                data-testid="new-session-time-input"
              />
            </div>
            <div>
              <Label className="font-bold text-sm">Liczba pytań</Label>
              <Input
                type="number" min={1} max={50}
                value={num} onChange={(e) => setNum(e.target.value)}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                data-testid="new-session-num-input"
                disabled={mode === "manual"}
              />
            </div>
            <div>
              <Label className="font-bold text-sm">Klasa</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger
                  className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
                  data-testid="new-session-class-select"
                >
                  <SelectValue placeholder="(bez przypisania)" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-[#0F172A] rounded-md">
                  <SelectItem value="none">(bez przypisania)</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`class-option-${c.name}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Anti-cheat: randomize question order per student */}
          <label
            className="brut bg-[#FEF3C7] rounded-md p-3 flex items-start gap-3 cursor-pointer"
            data-testid="randomize-order-toggle"
          >
            <input
              type="checkbox"
              checked={randomizeOrder}
              onChange={(e) => setRandomizeOrder(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0F172A] cursor-pointer"
              data-testid="randomize-order-checkbox"
            />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm inline-flex items-center gap-2">
                🎯 Losuj kolejność pytań per uczeń
                <span className="brut bg-white text-[10px] font-black uppercase px-1.5 py-0.5 rounded">
                  Anti-ściąganie
                </span>
              </div>
              <div className="text-xs text-[#334155] mt-0.5">
                Każdy uczeń dostanie pytania w innej kolejności — sąsiad nie ma jak podpatrzeć „które pytanie 3?".
                Zestaw jest identyczny dla wszystkich, zmienia się tylko kolejność.
              </div>
            </div>
          </label>

          {/* Anti-cheat: two disjoint groups A/B */}
          <label
            className="brut bg-[#F5D0FE] rounded-md p-3 flex items-start gap-3 cursor-pointer"
            data-testid="groups-toggle"
          >
            <input
              type="checkbox"
              checked={groupsEnabled}
              onChange={(e) => setGroupsEnabled(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0F172A] cursor-pointer"
              data-testid="groups-checkbox"
            />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm inline-flex items-center gap-2">
                👥 Grupy A/B (dwa kody, różne pytania)
                <span className="brut bg-white text-[10px] font-black uppercase px-1.5 py-0.5 rounded">
                  Max anti-ściąganie
                </span>
              </div>
              <div className="text-xs text-[#334155] mt-0.5">
                Kartkówka dostanie dwa kody — pół klasy wchodzi kodem A, druga połowa kodem B.
                Oba kody mają pytania z tych samych tematów, ale <b>całkowicie różne treści</b>.
                Wystartują i skończą się jednocześnie; wyniki lecą do wspólnej statystyki.
                {mode !== "manual" && <> Wymaga <b>2× więcej pytań</b> na temat w Twojej puli.</>}
                {mode === "manual" && <> W trybie ręcznym wybierzesz pytania osobno dla grupy A i B (poniżej).</>}
              </div>
            </div>
          </label>

          {/* Combo hint — appears whenever BOTH anti-cheat toggles are on, to
              dispel the "these are alternatives" misread. */}
          {randomizeOrder && groupsEnabled && (
            <div
              className="brut bg-[#D1FAE5] rounded-md p-3 flex items-start gap-2 text-xs"
              data-testid="anti-cheat-combo-hint"
            >
              <Dice5 className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <div>
                <b>Możesz łączyć obie opcje.</b> Grupy A/B rozdzielają <i>pule pytań</i>,
                a losowa kolejność miesza <i>sekwencję wewnątrz każdej grupy</i>. Razem to{" "}
                <b>maksymalna ochrona przed ściąganiem</b> — sąsiad nie zobaczy ani tych samych pytań,
                ani tej samej ich kolejności.
              </div>
            </div>
          )}

          {/* Mode selector — auto / cross-scope / manual */}
          <div className="brut bg-white rounded-md p-3 flex flex-wrap gap-2" data-testid="mode-selector">
            {[
              { key: "auto", label: "🎲 Automatyczny", sub: "Losowo z bieżącego zakresu" },
              { key: "cross", label: "🔀 Wieloprzekrojowy", sub: "Kwoty z różnych zakresów" },
              { key: "manual", label: "✋ Ręczny", sub: "Wybierz konkretne pytania" },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`brut brut-hover rounded-md px-3 py-2 text-xs font-bold flex-1 min-w-[140px] text-left ${
                  mode === m.key ? "bg-[#0F172A] text-white" : "bg-[#FFFBEB] hover:bg-[#FEF08A]"
                }`}
                data-testid={`mode-${m.key}`}
              >
                <div>{m.label}</div>
                <div className={`text-[10px] mt-0.5 font-normal ${
                  mode === m.key ? "text-white/70" : "text-[#64748B]"
                }`}>{m.sub}</div>
              </button>
            ))}
          </div>

          {/* Manual mode picker */}
          {mode === "manual" && (
            <div className="brut bg-[#F8FAFC] rounded-md p-4">
              <div className="mb-3">
                <Label className="font-bold text-sm">Wybór ręczny</Label>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {groupsEnabled
                    ? "Zaznacz pytania osobno dla grupy A i B. Pule muszą być rozłączne (max 50 pytań w każdej)."
                    : "Zaznacz pytania z DOWOLNEGO zakresu i tematu. Maksymalnie 50."}
                </div>
              </div>

              {groupsEnabled ? (
                <>
                  {/* A/B tab switcher */}
                  <div className="flex gap-2 mb-3" data-testid="manual-ab-tabs">
                    {[
                      { key: "A", label: "Grupa A", accent: "#DDD6FE", count: manualIds.length },
                      { key: "B", label: "Grupa B", accent: "#FBCFE8", count: manualIdsB.length },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setManualGroupTab(t.key)}
                        className={`brut brut-hover rounded-md px-4 py-2 text-sm font-black flex-1 flex items-center justify-between gap-2 ${
                          manualGroupTab === t.key
                            ? "text-[#0F172A] shadow-[4px_4px_0px_#0F172A]"
                            : "bg-white text-[#334155]"
                        }`}
                        style={
                          manualGroupTab === t.key
                            ? { backgroundColor: t.accent }
                            : undefined
                        }
                        data-testid={`manual-ab-tab-${t.key}`}
                      >
                        <span>👥 {t.label}</span>
                        <span className="text-[10px] font-black uppercase tracking-wide bg-white/70 border border-[#0F172A] px-1.5 py-0.5 rounded">
                          {t.count} / 50
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Render single picker instance, but swap selection state
                      based on active tab. Locked ids = the OTHER group's
                      picks, so overlap is impossible by construction. */}
                  {manualGroupTab === "A" ? (
                    <ManualQuestionPicker
                      key="picker-A"
                      scopes={scopes}
                      selectedIds={manualIds}
                      onChange={setManualIds}
                      max={50}
                      lockedIds={manualIdsB}
                      groupLabel="A"
                      groupAccent="#DDD6FE"
                    />
                  ) : (
                    <ManualQuestionPicker
                      key="picker-B"
                      scopes={scopes}
                      selectedIds={manualIdsB}
                      onChange={setManualIdsB}
                      max={50}
                      lockedIds={manualIds}
                      groupLabel="B"
                      groupAccent="#FBCFE8"
                    />
                  )}
                </>
              ) : (
                <ManualQuestionPicker
                  scopes={scopes}
                  selectedIds={manualIds}
                  onChange={setManualIds}
                  max={50}
                />
              )}
            </div>
          )}

          {/* Topic quotas — used by both auto and cross modes */}
          {mode !== "manual" && (
          <div className="brut bg-[#F8FAFC] rounded-md p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <Label className="font-bold text-sm">
                  {mode === "cross" ? "Kwoty tematów (wieloprzekrojowe)" : "Zakres pytań (tematy)"}
                </Label>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {mode === "cross"
                    ? "Dla każdego tematu wybierz z jakiego zakresu pobrać pytania. Suma musi się zgadzać z liczbą pytań."
                    : <>Zaznacz tematy i ustaw ile pytań z każdego. Suma musi się zgadzać z liczbą pytań.
                       Pomiń aby losować ze wszystkich.</>}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={distributeEvenly}
                className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-8 text-xs"
                data-testid="distribute-evenly-btn"
              >
                <Dice5 className="w-3 h-3 mr-1" strokeWidth={2.5} />Rozłóż równo
              </Button>
            </div>

            {/* Allocation summary */}
            <div className={`brut rounded-md px-3 py-2 mb-3 text-sm font-bold inline-flex items-center gap-2 ${
              exactlyMatched ? "bg-[#D1FAE5]" : overAllocated ? "bg-[#FEE2E2]" : "bg-[#FEF08A]"
            }`} data-testid="quota-summary">
              {overAllocated && <AlertTriangle className="w-4 h-4" strokeWidth={2.5} />}
              Przydzielono <span className="font-display">{allocated}</span> / <span className="font-display">{numNum}</span>
              {!overAllocated && remaining > 0 && Object.keys(quotas).length > 0 && (
                <span className="text-[#64748B] font-normal">· brakuje {remaining}</span>
              )}
              {exactlyMatched && <span className="text-[#10B981]">· OK</span>}
            </div>

            <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
              {topicsMeta.length === 0 && (
                <div className="col-span-full text-xs text-[#64748B]">Ładowanie tematów...</div>
              )}
              {topicsMeta.map((t) => {
                const selected = t.topic in quotas;
                const val = quotas[t.topic] ?? 0;
                return (
                  <div
                    key={t.topic}
                    className={`brut rounded-md p-3 flex items-center gap-2 ${selected ? "bg-white" : "bg-white/60"}`}
                    data-testid={`topic-row-${t.topic}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleTopic(t.topic)}
                      className={`brut w-8 h-8 flex items-center justify-center rounded-md font-bold flex-shrink-0 ${
                        selected ? "bg-[#10B981] text-white" : "bg-white"
                      }`}
                      data-testid={`toggle-topic-${t.topic}`}
                      aria-label={selected ? "Usuń temat" : "Dodaj temat"}
                    >
                      {selected ? <X className="w-3 h-3" strokeWidth={2.5} /> : <Plus className="w-3 h-3" strokeWidth={2.5} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate" title={t.topic}>{t.topic}</div>
                      <div className="text-xs text-[#64748B]">dostępnych: {t.count}</div>
                    </div>
                    {selected && (
                      <Input
                        type="number"
                        min={0}
                        max={t.count}
                        value={val}
                        onChange={(e) => setQuota(t.topic, e.target.value)}
                        className="brut-input border-2 border-[#0F172A] rounded-md h-9 w-16 text-center font-bold flex-shrink-0"
                        data-testid={`quota-input-${t.topic}`}
                      />
                    )}
                    {selected && mode === "cross" && (
                      <select
                        value={quotaScopes[t.topic] || scope}
                        onChange={(e) => setQuotaScopes((p) => ({ ...p, [t.topic]: e.target.value }))}
                        className="brut-input border-2 border-[#0F172A] rounded-md h-9 px-1 text-xs font-bold flex-shrink-0 max-w-[110px]"
                        data-testid={`quota-scope-${t.topic}`}
                        title="Zakres tego tematu"
                      >
                        {scopes.filter((s) => s.question_count > 0).map((s) => (
                          <option key={s.scope} value={s.scope}>{s.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
          <div className="flex-1 min-w-0">
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Nazwa szkicu (opcjonalnie)"
              className="brut-input border-2 border-[#0F172A] rounded-md h-11 text-sm"
              data-testid="draft-name-input"
            />
          </div>
          <Button
            onClick={saveDraft}
            disabled={savingDraft || overAllocated}
            variant="outline"
            className="brut border-2 border-[#0F172A] bg-[#FEF08A] hover:bg-[#FDE047] text-[#0F172A] font-bold rounded-md"
            data-testid="save-draft-btn"
          >
            {savingDraft ? "Zapisuję..." : (draftId ? "Zaktualizuj szkic" : "Zapisz jako szkic")}
          </Button>
          <Button
            onClick={create}
            disabled={
              creating ||
              (mode === "manual" ? manualIds.length === 0 :
               (overAllocated || (Object.keys(quotas).length > 0 && !exactlyMatched)))
            }
            className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
            data-testid="confirm-create-session-btn"
          >
            {creating ? "Tworzę..." : "Utwórz sesję"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
